import { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';

type SoundType = 'success' | 'error' | 'warning' | 'info' | 'test'; 

interface SoundContextType {
  soundEnabled: boolean;
  setSoundEnabled: (enabled: boolean) => void;
  soundVolume: number;
  setSoundVolume: (volume: number) => void;
  playSound: (type?: SoundType, customVolume?: number) => void;
  playTickSound: (volume: number) => void;
}

const SoundContext = createContext<SoundContextType | undefined>(undefined);

interface SoundProviderProps {
  children: ReactNode;
}

export const useSound = (): SoundContextType => {
  const context = useContext(SoundContext);
  if (!context) {
    throw new Error('useSound must be used within SoundProvider');
  }
  return context;
};

export const SoundProvider = ({ children }: SoundProviderProps) => {
  const [soundEnabled, setSoundEnabled] = useState<boolean>(() => {
    const saved = localStorage.getItem('soundEnabled');
    return saved !== null ? JSON.parse(saved) : true;
  });

  const [soundVolume, setSoundVolume] = useState<number>(() => {
    const saved = localStorage.getItem('soundVolume');
    return saved !== null ? parseInt(saved, 10) : 50;
  });

  useEffect(() => {
    localStorage.setItem('soundEnabled', JSON.stringify(soundEnabled));
  }, [soundEnabled]);

  useEffect(() => {
    localStorage.setItem('soundVolume', soundVolume.toString());
  }, [soundVolume]);

  const playSound = (type: SoundType = 'success', customVolume?: number): void => {
    if (!soundEnabled && type !== 'test') return; 

    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) {
      console.warn('Web Audio API is not supported in this browser');
      return;
    }

    const audioContext = new AudioContextClass();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    const soundConfig: Record<SoundType, { frequency: number; duration: number }> = {
      success: { frequency: 800, duration: 0.15 },
      error: { frequency: 300, duration: 0.3 },
      warning: { frequency: 600, duration: 0.2 },
      info: { frequency: 500, duration: 0.15 },
      test: { frequency: 650, duration: 0.1 }
    };

    const config = soundConfig[type] || soundConfig.info;
    
    oscillator.frequency.value = config.frequency;
    oscillator.type = 'sine';
    
    const volume = (customVolume ?? soundVolume) / 100;
    gainNode.gain.setValueAtTime(volume * 0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + config.duration);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + config.duration);

    setTimeout(() => {
      audioContext.close();
    }, config.duration * 1000 + 100);
  };

  const playTickSound = (volume: number): void => {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;

    const audioContext = new AudioContextClass();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    
    const vol = (volume / 100) * 0.25;
    const duration = 0.03; 
    
    gainNode.gain.setValueAtTime(vol, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + duration);

    setTimeout(() => {
      audioContext.close();
    }, duration * 1000 + 50);
  };

  const value: SoundContextType = {
    soundEnabled,
    setSoundEnabled,
    soundVolume,
    setSoundVolume,
    playSound,
    playTickSound
  };

  return <SoundContext.Provider value={value}>{children}</SoundContext.Provider>;
};